/**
 * em-family-configurator.js — P2a Ring 1: the PDP presents its product FAMILY
 * (same custom.product_core_name) as one product with option rows — LENGTH /
 * POCKETS / COLOUR — under the size row. Tapping an option SPA-swaps to the
 * sibling product (_v2SwapToHandle); the shopper's size follows her (size
 * persistence shipped earlier), so it reads as variant selection.
 *
 * Locked rules (28 Jul 2026, revised 3 Aug 2026):
 *  D1 hide-mode — options that cannot be SOLD in the size the buy box is
 *     currently presenting are HIDDEN. Revised 27 Aug 2026 on two counts:
 *       (a) it now keys off the PRESENTED size (activeSize: her trusted pick,
 *           else the preselected one), not a trusted pick alone. The size row
 *           preselects the most popular size and arms the ATC with a price,
 *           so offering lengths and colours that size cannot buy was a lie —
 *           and a dead end. (The old rule made sense when auto-select was
 *           arbitrary, first-in-DOM.)
 *       (b) "available" means PURCHASABLE (sellableAt: in stock OR
 *           pre-orderable), not in-stock-only. The old test hid pre-order
 *           lengths the shop can genuinely sell.
 *     One exception: the combo she is currently ON stays visible-struck when
 *     dead (the page cannot hide its own state). In-stock-only remains the
 *     test for "ready to ship now" copy and the green escape hints.
 *  D5 no auto-swap, STRICT (3 Aug) — a tap swaps ONLY when the exact combo
 *     exists (her current combo with just that one axis changed). When it
 *     doesn't, the tap becomes a PENDING selection: the chip highlights, the
 *     status block says where that choice IS available, and SHE taps a chip
 *     to change products. No more silent cross-length/garment jumps
 *     ("selected no pockets, landed on 7/8" — UAT 3 Aug).
 *  D5.1 stock-aware landing (24 Aug) — when the exact combo EXISTS but is
 *     dead in her picked size, the tap lands on the closest in-stock member
 *     carrying her tapped value (fewest other-axis changes, deepest stock on
 *     ties) instead of the dead page. The tapped axis is always honoured;
 *     visibility (D1, value-wide) and landing now keep the same promise.
 *  Status messaging — ALL availability copy renders in #em-status-block
 *     ABOVE the ATC (owned half: #em-status-alt; packprice writes the
 *     limited-release lines into #em-status-main). The old green .fc-rescue
 *     line under the rows is gone.
 *  In-stock = catalog qty > 0. Bucket rules: family only — no lifestage
 *  jumps except Ring 2 (below).
 *
 * Data: window.__emPackCatalog (pack-catalog.liquid, on every page) +
 * window.__emPreorderInfo (packprice: selection state, size label, sku).
 * Scope: EVERY leggings/shorts family with 2+ members. Tops carry no axes
 * and never render rows. Shorts rules (locked): bike shorts = shorts;
 * long shorts is the only shorts length variation.
 */
(function () {
  'use strict';

  // LENGTH is one spectrum across the whole bottoms RANGE (28 Jul): a shopper
  // on Maternity Shorts also sees 3/4 / 7/8 / full length — tapping one swaps
  // to the leggings sibling. Short-to-long order.
  var AXES = [
    { key: 'length', label: 'length', order: ['shorts', 'long shorts', '3/4', '7/8', 'full length'] },
    { key: 'feature', label: 'pockets', order: ['with pockets', 'no pockets'] },
    { key: 'colour', label: 'colour', order: ['black'] } // black first, rest alpha
  ];
  var SPECTRUM = ['shorts', 'long shorts', '3/4', '7/8', 'full length'];
  var SWATCH = {
    black: '#26262a', olive: '#6b7250', twilight: '#3d4a63', spearmint: '#9fc6b8',
    caramel: '#b07f4f', rose: '#d9a0a0', plum: '#7d5670', nude: '#d9c2ad',
    berry: '#a04a5e', lilac: '#b9a6cc', white: '#f2f2f2', charcoal: '#4a4a4e'
  };
  var HOST_ID = 'em-family-config';

  // Pending selection = the shopper tapped an option whose exact combo does
  // not exist as a product. {axis, value} until she resolves it (chip tap,
  // size change, re-tap of her current value) or the product swaps.
  var pending = null;

  function catalog() { return window.__emPackCatalog || null; }
  // Current product handle. pdpMeta first: on the HOME SPA the path is "/"
  // until the first swap, so the URL alone goes blind exactly where the SPA
  // matters most. pdpMeta is re-parsed by the swap code, so it tracks both
  // surfaces from the same source of truth.
  function currentHandle() {
    if (window.pdpMeta && window.pdpMeta.handle) return window.pdpMeta.handle;
    var m = (location.pathname || '').match(/\/products\/([^\/?#]+)/);
    return m ? m[1] : null;
  }
  function norm(s) { return String(s == null ? '' : s).trim().toLowerCase(); }

  function garmentOf(p) {
    var b = String(p.bucket || '');
    if (b.indexOf('|leggings') >= 0) return 'leggings';
    if (b.indexOf('|shorts') >= 0) return 'shorts';
    return null;                                          // tops etc: no configurator
  }

  // Family member view over a catalog product (locked normalisation rules)
  function memberOf(p, garment) {
    var len;
    if (garment === 'shorts') {
      var raw = norm(p.length);
      len = raw === 'long shorts' ? 'long shorts' : 'shorts';   // bike shorts = shorts
    } else {
      len = norm(p.length) || 'full length';                    // missing length = full length
    }
    return {
      handle: p.handle,
      length: len,
      feature: norm(p.feature) === 'with pockets' ? 'with pockets' : 'no pockets',
      colour: norm(p.color) || 'black',                         // missing colour = black
      p: p
    };
  }

  // Range key: coreName minus its trailing garment word — "Maternity Leggings"
  // and "Maternity Shorts" both key to "maternity", so the LENGTH row spans the
  // whole bottoms spectrum of the range. The grid picker's separate
  // shorts/leggings entries are untouched; this only widens the option rows.
  function rangeKey(coreName) {
    return norm(coreName).replace(/\s+(leggings|shorts)$/, '');
  }

  function familyFor(handle) {
    var cat = catalog();
    if (!cat || !cat.products) return null;
    var cur = null;
    for (var i = 0; i < cat.products.length; i++) {
      if (cat.products[i].handle === handle) { cur = cat.products[i]; break; }
    }
    if (!cur) return null;
    if (!garmentOf(cur)) return null;                     // bottoms only; tops never configure
    var range = rangeKey(cur.coreName);
    if (!range) return null;
    var members = [], seen = {};
    cat.products.forEach(function (p) {
      var g = garmentOf(p);
      if (!g) return;                                     // bottoms only
      if (rangeKey(p.coreName) !== range) return;
      if (p.bundleEligible === false) return;             // drops never enter the configurator
      if (seen[p.handle]) return;
      seen[p.handle] = true;
      members.push(memberOf(p, g));
    });
    if (members.length < 2) return null;
    var me = null;
    members.forEach(function (m) { if (m.handle === handle) me = m; });
    if (!me) return null;
    return { range: range, members: members, current: me };
  }

  // Sellable-now check for a member at a size (catalog qty > 0)
  function inStockAt(member, size) {
    if (!size) return true;
    var vs = member.p.variants || [];
    for (var i = 0; i < vs.length; i++) {
      if (String(vs[i].size || '').toUpperCase() === size && (vs[i].qty || 0) > 0) return true;
    }
    return false;
  }

  // The size SHE picked — trusted picks only. Still the signal for anything
  // that must not act on a size she never chose.
  function herSize() {
    var p = window.__emUserSizePick;
    return (p && p.size) ? String(p.size).toUpperCase() : null;
  }

  // The size the page is CURRENTLY PRESENTING — her trusted pick, else the
  // preselected one. Row visibility keys off this (Mark, 27 Aug): the buy box
  // shows a selected size with a live price, so an option row that offers
  // lengths and colours which cannot be bought in that size is simply wrong.
  // (Before the popularity preselect landed, auto-select was arbitrary —
  // first-in-DOM — so compressing the matrix on it would have hidden the
  // range behind a size nobody chose. A deliberate M is a different thing.)
  function activeSize() {
    var sel = document.querySelector('#size-buttons button.selected');
    if (sel) {
      var lbl = (sel.textContent || '').replace(/\s*\(\d+\)\s*/, '').trim().toUpperCase();
      if (lbl) return lbl;
    }
    var info = window.__emPreorderInfo;
    if (info && info.size) return String(info.size).toUpperCase();
    return herSize();
  }

  // Available to sell = Shopify-purchasable at that size: in stock now OR
  // sellable as a pre-order/continue-selling line. This is what the option
  // rows filter on. In-stock-only (inStockAt) stays the test for anything
  // that promises "ready to ship now" — the two are deliberately different.
  function sellableAt(member, size) {
    if (!size) return true;
    var vs = member.p.variants || [];
    for (var i = 0; i < vs.length; i++) {
      if (String(vs[i].size || '').toUpperCase() === size && vs[i].avail) return true;
    }
    return false;
  }
  // Size for the status copy: the SELECTED size (auto-select included) so the
  // alternatives line always matches the limited-release lines beside it.
  function copySize() {
    var info = window.__emPreorderInfo;
    if (info && info.size) return String(info.size).toUpperCase();
    return herSize();
  }

  function distinctValues(fam, axis) {
    var seen = {}, out = [];
    fam.members.forEach(function (m) {
      var v = m[axis.key];
      if (!seen[v]) { seen[v] = true; out.push(v); }
    });
    out.sort(function (a, b) {
      var ia = axis.order.indexOf(a), ib = axis.order.indexOf(b);
      if (ia < 0 && ib < 0) return a < b ? -1 : 1;
      if (ia < 0) return 1; if (ib < 0) return -1;
      return ia - ib;
    });
    return out;
  }

  // Row visibility: does ANY member carrying this value sell at that size?
  function anyWithValueInSize(fam, axisKey, value, size) {
    for (var i = 0; i < fam.members.length; i++) {
      var m = fam.members[i];
      if (m[axisKey] === value && sellableAt(m, size)) return true;
    }
    return false;
  }

  // The member that IS the current combo with exactly ONE axis changed —
  // the only swap a plain option tap may perform (D5 strict).
  function exactMember(fam, axisKey, value) {
    for (var i = 0; i < fam.members.length; i++) {
      var m = fam.members[i];
      if (m[axisKey] !== value) continue;
      var same = true;
      AXES.forEach(function (o) { if (o.key !== axisKey && m[o.key] !== fam.current[o.key]) same = false; });
      if (same) return m;
    }
    return null;
  }

  // Closest member carrying a value (fewest other-axis changes from the
  // current combo; deepest stock breaks ties) that passes `test`.
  function bestWith(fam, axisKey, value, size, test) {
    var best = null, bestDiffs = 9, bestQty = -1;
    fam.members.forEach(function (m) {
      if (m[axisKey] !== value) return;
      if (!test(m, size)) return;
      var diffs = 0;
      AXES.forEach(function (o) { if (o.key !== axisKey && m[o.key] !== fam.current[o.key]) diffs++; });
      var v = variantAt(m, size);
      var qty = (v && v.qty) || 0;
      if (diffs < bestDiffs || (diffs === bestDiffs && qty > bestQty)) {
        best = m; bestDiffs = diffs; bestQty = qty;
      }
    });
    return best;
  }

  // The D5.1 landing when the exact combo exists but is dead in her size:
  // ready-to-ship first, then merely BUYABLE (pre-order / continue-selling).
  // The second tier matters now that the rows offer a value whenever ANY
  // member sells it — tapping a visible option must not dead-end on a
  // sold-out page while a buyable sibling exists (27 Aug).
  function bestBuyableWith(fam, axisKey, value, size) {
    return bestWith(fam, axisKey, value, size, inStockAt)
      || bestWith(fam, axisKey, value, size, sellableAt);
  }

  // Single-axis escapes from a dead current combo (for the option-chip glow)
  function escapes(fam, size) {
    var out = [];
    AXES.forEach(function (ax) {
      distinctValues(fam, ax).forEach(function (v) {
        if (v === fam.current[ax.key]) return;
        var m = exactMember(fam, ax.key, v);
        if (m && inStockAt(m, size)) out.push({ axis: ax.key, value: v });
      });
    });
    return out;
  }

  // Family members in stock in her size, labelled by how they differ from the
  // current combo ("7/8", "no pockets", "full length, no pockets"). Powers the
  // ready-to-ship chips in the status block. Closest (fewest changes) first,
  // then the length spectrum order; capped at 4.
  function altChips(fam, size) {
    var out = [], seen = {};
    fam.members.forEach(function (m) {
      if (m.handle === fam.current.handle) return;
      if (!inStockAt(m, size)) return;
      var diff = [];
      AXES.forEach(function (ax) { if (m[ax.key] !== fam.current[ax.key]) diff.push(m[ax.key]); });
      if (!diff.length) return;
      var label = diff.join(', ');
      if (seen[label]) return;
      seen[label] = true;
      var v = size ? variantAt(m, size) : null;
      out.push({ handle: m.handle, label: label, diffs: diff.length, len: SPECTRUM.indexOf(m.length), m: m, qty: (v && v.qty) || 0 });
    });
    out.sort(function (a, b) { return (a.diffs - b.diffs) || (a.len - b.len); });
    return out.slice(0, 4);
  }

  // Pre-order config entry for a sku. `preorderConfig` is a top-level `let`
  // in packprice's classic script — visible here through the global lexical
  // environment; guard for load order anyway.
  function cfgEntryFor(sku) {
    try {
      var cfg = (typeof preorderConfig !== 'undefined' && Array.isArray(preorderConfig)) ? preorderConfig : null;
      if (!cfg || !sku) return null;
      for (var i = 0; i < cfg.length; i++) {
        var e = cfg[i];
        if (e && e.sku === sku && (e.qty || 0) > 0) return e;
      }
    } catch (e) {}
    return null;
  }
  function variantAt(member, size) {
    var vs = member.p.variants || [];
    for (var i = 0; i < vs.length; i++) {
      if (String(vs[i].size || '').toUpperCase() === size) return vs[i];
    }
    return null;
  }

  // Family members whose HER-SIZE variant is not in stock but IS pre-orderable
  // (Shopify-purchasable + sku in preorder-config): the "Ready to ship {window}"
  // group rendered under the ready-now chips (UAT 3 Aug round 2). Grouped by
  // the config's _ship phrase (earliest ETA first); tokenless legacy entries
  // fall into a generic "Available to order" group. valueFilter narrows to a
  // pending value and labels like pendingChips.
  function preorderGroups(fam, size, valueFilter) {
    if (!size) return [];
    var items = [], seen = {};
    fam.members.forEach(function (m) {
      if (m.handle === fam.current.handle) return;
      if (valueFilter && m[valueFilter.axis] !== valueFilter.value) return;
      if (inStockAt(m, size)) return;                // in-stock lives in the ready-now group
      var v = variantAt(m, size);
      if (!v || !v.avail) return;                    // not purchasable → plain sold out
      var e = cfgEntryFor(v.sku);
      if (!e) return;
      var diff = [];
      AXES.forEach(function (ax) {
        if (valueFilter && ax.key === valueFilter.axis) return;
        if (m[ax.key] !== fam.current[ax.key]) diff.push(m[ax.key]);
      });
      var label = diff.length ? diff.join(', ') : m.length;
      var ship = e._ship || '';
      var key = label + '|' + ship;
      if (seen[key]) return;
      seen[key] = true;
      items.push({ handle: m.handle, label: label, ship: ship, eta: e._eta || '9999', diffs: diff.length, len: SPECTRUM.indexOf(m.length), m: m });
    });
    items.sort(function (a, b) { return (a.diffs - b.diffs) || (a.len - b.len); });
    var groups = {}, order = [];
    items.forEach(function (it) {
      if (!groups[it.ship]) { groups[it.ship] = { ship: it.ship, eta: it.eta, chips: [] }; order.push(it.ship); }
      var g = groups[it.ship];
      if (it.eta < g.eta) g.eta = it.eta;
      if (g.chips.length < 4) g.chips.push(it);
    });
    return order.map(function (k) { return groups[k]; }).sort(function (a, b) { return a.eta < b.eta ? -1 : 1; });
  }
  function flatGroups(groups) {
    var out = [];
    groups.forEach(function (g) { out = out.concat(g.chips); });
    return out;
  }
  function preorderGroupsHtml(groups) {
    var h = '';
    groups.forEach(function (g) {
      h += '<div class="es-line es-pos">' + (g.ship ? 'Ready to ship ' + escHtml(g.ship) : 'Available to order') + '</div>'
        + '<div class="es-chips">' + chipsHtml(g.chips) + '</div>';
    });
    return h;
  }

  // Members carrying the PENDING value (any other axes), in stock in her
  // size — the "she taps to change" chips. Labelled by everything that would
  // change besides the pending value itself; falls back to the length.
  function pendingChips(fam, size) {
    if (!pending) return [];
    var out = [], seen = {};
    fam.members.forEach(function (m) {
      if (m[pending.axis] !== pending.value) return;
      if (!inStockAt(m, size)) return;
      var diff = [];
      AXES.forEach(function (ax) {
        if (ax.key === pending.axis) return;
        if (m[ax.key] !== fam.current[ax.key]) diff.push(m[ax.key]);
      });
      var label = diff.length ? diff.join(', ') : m.length;
      if (seen[label]) return;
      seen[label] = true;
      var v = size ? variantAt(m, size) : null;
      out.push({ handle: m.handle, label: label, diffs: diff.length, len: SPECTRUM.indexOf(m.length), m: m, qty: (v && v.qty) || 0 });
    });
    out.sort(function (a, b) { return (a.diffs - b.diffs) || (a.len - b.len); });
    return out.slice(0, 4);
  }

  // ── P2c funnel events ─────────────────────────────────────────────────
  // Emits through the existing wardrobe pipeline; shown-events dedupe per
  // page/size/state so re-renders don't spam rows.
  var _emitted = {};
  function emit(type, props, dedupeKey) {
    try {
      if (dedupeKey) {
        if (_emitted[dedupeKey]) return;
        _emitted[dedupeKey] = true;
      }
      props = props || {};
      props.source_surface = 'family_configurator';
      if (typeof window.__scvEmit === 'function') window.__scvEmit(type, props);
    } catch (e) {}
  }

  // ── Ring 2: forward-stage rescue cards (agreed 28 Jul) ────────────────
  // Fires ONLY when the whole family is dead in her size. Last-resort
  // exception to the no-lifestage-jump rule: candidates come from the NEXT
  // stage(s), same garment, never backwards, outside the current range —
  // in stock in her exact size, deepest first, max three.
  var RING2_FORWARD = { maternity: ['postpartum', 'mum'], postpartum: ['mum'], mum: [] };
  var CUR_SYM = { AUD: '$', USD: '$', CAD: '$', NZD: '$', GBP: '£', EUR: '€', SGD: 'S$' };
  function curSym() {
    try { return CUR_SYM[(window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD'] || '$'; } catch (e) { return '$'; }
  }
  function stageOfProduct(p) { return String(p.bucket || '').split('|')[0]; }

  function ring2Candidates(fam, size) {
    var cat = catalog();
    if (!cat || !cat.products) return [];
    var stage = stageOfProduct(fam.current.p);
    var garment = garmentOf(fam.current.p);
    var stages = RING2_FORWARD[stage] || [];
    var out = [];
    stages.forEach(function (st) {
      cat.products.forEach(function (p) {
        if (p.bundleEligible === false) return;
        if (garmentOf(p) !== garment) return;
        if (String(p.bucket || '').indexOf(st + '|') !== 0) return;
        if (rangeKey(p.coreName) === fam.range) return;   // outside the dead family
        var best = null;
        (p.variants || []).forEach(function (v) {
          if (String(v.size || '').toUpperCase() !== size) return;
          if (!(typeof v.qty === 'number' && v.qty > 0)) return;
          if (!best || v.qty > best.qty) best = v;
        });
        if (best) out.push({ handle: p.handle, vid: best.id, qty: best.qty, price: best.price,
          name: p.simpleName || p.coreName || p.title, tagline: p.tagline || '', image: p.image || '' });
      });
    });
    out.sort(function (a, b) { return (b.qty || 0) - (a.qty || 0); });
    // dedupe by handle, cap 3
    var seen = {}, top = [];
    out.forEach(function (c) { if (!seen[c.handle] && top.length < 3) { seen[c.handle] = true; top.push(c); } });
    return top;
  }

  function renderRing2(fam, size, familyDead) {
    var old = document.getElementById('em-family-ring2');
    if (!familyDead || !size) { if (old) old.remove(); return; }
    var cards = ring2Candidates(fam, size);
    if (!cards.length) { if (old) old.remove(); return; }
    var atc = document.getElementById('add-to-cart-button');
    if (!atc) { if (old) old.remove(); return; }
    var host = old;
    if (!host) {
      host = document.createElement('div');
      host.id = 'em-family-ring2';
      // directly under the (disabled) ATC, per the approved S4 drawing
      atc.parentNode.insertBefore(host, atc.nextSibling);
    }
    var sym = curSym();
    var h = '<div class="fr2-cap">' + size + ' is ready to ship in these</div><div class="fr2-row">';
    cards.forEach(function (c) {
      h += '<button type="button" class="fr2-card" data-handle="' + c.handle + '">'
        + (c.image ? '<img src="' + c.image + '" alt="" loading="lazy">' : '<span class="fr2-ph"></span>')
        + '<span class="fr2-nm">' + c.name + '</span>'
        + '<span class="fr2-pr">' + sym + Math.ceil(c.price || 0) + (c.tagline ? ' · ' + String(c.tagline).slice(0, 26) : '') + '</span>'
        + '</button>';
    });
    h += '</div>';
    host.innerHTML = h;
    emit('fc_ring2_shown', { handle: fam.current.handle, size: size, cards: cards.map(function (c) { return c.handle; }).join(',') },
      'r2|' + fam.current.handle + '|' + size + '|' + cards.map(function (c) { return c.handle; }).join(','));
    host.querySelectorAll('.fr2-card').forEach(function (btn) {
      btn.addEventListener('click', function (ev) {
        ev.preventDefault();
        var to = btn.getAttribute('data-handle');
        emit('fc_ring2_tap', { from_handle: fam.current.handle, to_handle: to, size: size });
        if (typeof window._v2SwapToHandle === 'function') window._v2SwapToHandle(to, { preserveScroll: true });
        else location.href = '/products/' + to;
      });
    });
  }

  function ensureStyles() {
    if (document.getElementById('em-fc-style')) return;
    var css = ''
      + '#' + HOST_ID + '{margin:2px 0 0;font-family:system-ui,-apple-system,sans-serif;}'
      + '#' + HOST_ID + ' .fc-lbl{font-size:10px;letter-spacing:.09em;text-transform:uppercase;color:#6b7280;margin:12px 0 5px;}'
      + '#' + HOST_ID + ' .fc-row{display:flex;gap:7px;flex-wrap:wrap;align-items:center;}'
      + '#' + HOST_ID + ' .fc-opt{font-size:13px;border:1px solid #d1d5db;border-radius:4px;padding:6px 13px;background:#fff;color:#1f2937;cursor:pointer;line-height:1.2;}'
      // Selected = INVERT (Mark, UAT 3 Aug): white/black flips to black/white
      // so "selected" never reads as merely emphasised.
      + '#' + HOST_ID + ' .fc-opt.sel{background:#1f2937;border-color:#1f2937;color:#fff;font-weight:700;}'
      + '#' + HOST_ID + ' .fc-opt.dead{text-decoration:line-through;opacity:.45;cursor:default;}'
      // Selected-but-sold-out: inverted too, grey text on black — reads
      // "this is your selection, it's out of stock", not "unavailable button".
      + '#' + HOST_ID + ' .fc-opt.sel.dead{background:#1f2937;border-color:#1f2937;color:#9ca3af;opacity:1;}'
      + '#' + HOST_ID + ' .fc-opt.go{border-color:#3D9A8B;color:#3D9A8B;font-weight:600;}'
      + '#' + HOST_ID + ' .fc-dot{width:22px;height:22px;border-radius:50%;border:1px solid #d1d5db;padding:0;cursor:pointer;position:relative;flex:none;}'
      + '#' + HOST_ID + ' .fc-dot.sel:after{content:"";position:absolute;inset:-5px;border:2px solid #1f2937;border-radius:50%;}'
      + '#' + HOST_ID + ' .fc-dot.go:after{content:"";position:absolute;inset:-5px;border:2px solid #3D9A8B;border-radius:50%;}'
      + '#' + HOST_ID + ' .fc-dot.dead{opacity:.35;cursor:default;}'
      + '#em-status-alt .es-line{font-size:12.5px;line-height:1.45;color:#6b7280;margin:2px 0 0;}'
      + '#em-status-alt .es-pos{color:#3D9A8B;font-weight:600;}'
      + '#em-status-alt .es-chips{display:flex;gap:6px;flex-wrap:wrap;align-items:center;margin:6px 0 0;}'
      + '#em-status-alt .es-chip{font-size:12.5px;border:1px solid #3D9A8B;border-radius:4px;padding:4px 10px;background:#fff;color:#3D9A8B;font-weight:600;cursor:pointer;line-height:1.2;}'
      // Ready-now chips as mini product tiles: shot on top, label under.
      + '#em-status-alt .es-chip-img{display:flex;flex-direction:column;align-items:center;gap:5px;padding:5px 5px 7px;width:88px;}'
      + '#em-status-alt .es-chip-img img{width:100%;height:96px;object-fit:cover;border-radius:3px;display:block;}'
      + '#em-status-alt .es-chip-img .es-cl{font-size:11px;line-height:1.25;text-align:center;}'
      // The persuasion card standing at the end of the block (no longer
      // inside the chips row) keeps clear air above it.
      + '#em-status-alt > .es-badge{margin-top:8px;}'
      // The stockout persuasion card: one chip grown into a green mini-card.
      + '#em-status-alt .es-badge{flex:1 1 100%;display:flex;flex-direction:column;gap:4px;align-items:flex-start;text-align:left;border:2px solid #3D9A8B;border-radius:6px;background:#f0f8f5;padding:10px 12px;cursor:pointer;font-family:inherit;}'
      + '#em-status-alt .es-badge .es-bk{font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#3D9A8B;font-weight:700;}'
      + '#em-status-alt .es-badge .es-bl{font-size:13.5px;color:#1f2937;font-weight:700;}'
      + '#em-status-alt .es-badge .es-bs{font-size:12.5px;line-height:1.45;color:#1f2937;}'
      + '#em-status-alt .es-badge .es-bf{font-size:11.5px;line-height:1.4;color:#374151;padding-left:15px;position:relative;}'
      + '#em-status-alt .es-badge .es-bf:before{content:"\\2713";position:absolute;left:0;top:0;color:#3D9A8B;font-weight:700;}'
      + '#em-status-alt .es-badge .es-bc{font-size:12.5px;color:#3D9A8B;font-weight:700;margin-top:2px;}'
      // The named switch action: visual affordance inside the card (span,
      // click bubbles to the card).
      + '#em-status-alt .es-swbtn{display:block;width:100%;background:#3D9A8B;color:#fff;border:none;border-radius:5px;padding:11px 12px;font-size:13.5px;font-weight:700;cursor:pointer;font-family:inherit;text-align:center;line-height:1.2;}'
      + '#em-status-alt .es-badge .es-swbtn{margin-top:6px;}'
      // State B's "or" label above the ready-now tiles (YOUR PICK / OR
      // READY TO SHIP TODAY reading grammar).
      + '#em-status-alt .es-lbl{font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#3D9A8B;font-weight:700;margin:8px 0 4px;}'
      + '#em-family-ring2{margin:12px 0 0;font-family:system-ui,-apple-system,sans-serif;}'
      + '#em-family-ring2 .fr2-cap{font-size:12px;font-weight:700;color:#3D9A8B;margin:0 0 7px;}'
      + '#em-family-ring2 .fr2-row{display:flex;gap:8px;}'
      + '#em-family-ring2 .fr2-card{flex:1;min-width:0;border:1px solid #3D9A8B;border-radius:6px;background:#fff;padding:6px;font-size:11px;line-height:1.35;cursor:pointer;text-align:left;display:flex;flex-direction:column;gap:3px;}'
      + '#em-family-ring2 .fr2-card img{width:100%;height:72px;object-fit:cover;border-radius:4px;display:block;}'
      + '#em-family-ring2 .fr2-ph{width:100%;height:72px;border-radius:4px;background:#f3f4f6;display:block;}'
      + '#em-family-ring2 .fr2-nm{font-weight:600;color:#1f2937;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}'
      + '#em-family-ring2 .fr2-pr{color:#6b7280;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}';
    var el = document.createElement('style');
    el.id = 'em-fc-style';
    el.textContent = css;
    document.head.appendChild(el);
  }

  function escHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function syncBlockVisibility() {
    var blk = document.getElementById('em-status-block');
    if (!blk) return;
    var main = document.getElementById('em-status-main');
    var alt = document.getElementById('em-status-alt');
    blk.style.display = ((main && main.innerHTML) || (alt && alt.innerHTML)) ? '' : 'none';
  }

  function chipsHtml(chips, withImages) {
    var h = '';
    chips.forEach(function (c) {
      // Ready-now chips carry the product shot stacked over the label (Mark,
      // 24 Aug) — the shopper is comparing garments she can't see from a
      // text diff like "3/4, no pockets". Pre-order group chips stay text.
      var img = withImages ? ((c.m && c.m.p && c.m.p.image) || '') : '';
      h += '<button type="button" class="es-chip' + (img ? ' es-chip-img' : '') + '" data-handle="' + escHtml(c.handle) + '">'
        + (img ? '<img src="' + escHtml(img) + '" alt="" loading="lazy">' : '')
        + '<span class="es-cl">' + escHtml(c.label) + '</span></button>';
    });
    return h;
  }

  function wireChips(alt, fam, size, kind) {
    alt.querySelectorAll('.es-chip').forEach(function (btn) {
      btn.addEventListener('click', function (ev) {
        ev.preventDefault();
        var to = btn.getAttribute('data-handle');
        emit('fc_alt_tap', { kind: kind, from_handle: fam.current.handle, to_handle: to, size: size || '' });
        pending = null;
        if (typeof window._v2SwapToHandle === 'function') window._v2SwapToHandle(to, { preserveScroll: true });
        else location.href = '/products/' + to;
      });
    });
  }

  // Re-run packprice's own selection pipeline (ATC label/disable, status
  // lines) after a pending selection is abandoned. Synthetic click — not a
  // trusted pick, so it never masquerades as a shopper size choice.
  function restoreSelection() {
    var sel = document.querySelector('#size-buttons button.selected');
    if (sel) { try { sel.click(); return; } catch (e) {} }
    // No size selected: put the ATC back to its pre-pending resting state.
    var atc = document.getElementById('add-to-cart-button');
    if (atc) {
      atc.classList.add('disabled');
      atc.textContent = (window.emStrings && window.emStrings.selectSize) || 'Select a size';
    }
    try { if (typeof updatePreorderNote === 'function') updatePreorderNote(); } catch (e) {}
  }

  // ── Switch badge — the stockout persuasion card ─────────────────────────
  // When her pick is out of stock / pre-order, ONE green chip grows into a
  // card: kicker + scene + true-fact bullets + consensus close, resolved from
  // the copy bank in em-switch-copy.js (docs/EMAMACO-SWITCH-COPY-BANK.md).
  // Target = the in-stock alternative with the deepest stock in her size
  // (most to shift, least restock risk); falls back to the earliest pre-order
  // chip with a swapped close. English only — the DE storefront skips it.
  var _pageSeed = Math.random();   // variant rotation: stable per pageview

  function bank() { return window.__emSwitchCopy || null; }

  function seasonGroup() {
    var c = String(window.emCountry || 'AU').toUpperCase();
    if (c === 'AU' || c === 'NZ') return 'south';
    if (c === 'SG') return 'equator';
    return 'north';
  }

  function geoTerms() {
    var b = bank();
    if (!b || !b.terms) return {};
    var c = String(window.emCountry || '').toUpperCase();
    return b.terms[c] || b.terms.DEFAULT || {};
  }

  function fillTokens(s, size, extra) {
    var t = geoTerms();
    var out = String(s || '')
      .replace(/\{size\}/g, size || 'yours')
      .replace(/\{pram\}/g, t.pram || 'pram')
      .replace(/\{card\}/g, t.card || 'cards')
      .replace(/\{schoolrun\}/g, t.schoolrun || 'school run')
      .replace(/\{checkup\}/g, t.checkup || 'check-up');
    if (extra) {
      Object.keys(extra).forEach(function (k) {
        out = out.replace(new RegExp('\\{' + k + '\\}', 'g'), extra[k]);
      });
    }
    // A token substituted at a sentence start ("walk. {pram} out the door")
    // must not read lowercase — sentence-case after ., ! and ?.
    return out.replace(/([.!?]\s+)([a-z])/g, function (m, a, ch) { return a + ch.toUpperCase(); });
  }

  // Pregnancy week at a future date, from the sizing-wizard person profile
  // (sessionStorage convomesh_person: lifestage + milestoneDate = due date).
  function weeksAt(dateIso) {
    try {
      var raw = sessionStorage.getItem('convomesh_person');
      if (!raw) return null;
      var p = JSON.parse(raw);
      if (p && p.person) p = p.person;
      if (!p || p.lifestage !== 'pregnant' || !p.milestoneDate) return null;
      var due = new Date(p.milestoneDate + 'T00:00:00');
      var at = new Date(dateIso + 'T00:00:00');
      if (isNaN(due) || isNaN(at)) return null;
      var w = Math.round(40 - (due - at) / (7 * 86400000));
      return (w >= 4 && w <= 42) ? w : null;
    } catch (e) { return null; }
  }

  function dirOf(fromLen, toLen) {
    var a = SPECTRUM.indexOf(fromLen), b2 = SPECTRUM.indexOf(toLen);
    if (a < 0 || b2 < 0 || a === b2) return 'same';
    return b2 > a ? 'longer' : 'shorter';
  }

  function pickCell(stage, from, to) {
    var b = bank();
    if (!b || !b.cells) return null;
    var best = null, bestScore = -1;
    for (var i = 0; i < b.cells.length; i++) {
      var c = b.cells[i];
      if (c.stage !== stage) continue;
      var s = 0, okc = true;
      ['length', 'feature'].forEach(function (k) {
        if (c.from && c.from[k] != null) { if (from[k] !== c.from[k]) okc = false; else s += 2; }
        if (c.to && c.to[k] != null) { if (to[k] !== c.to[k]) okc = false; else s += 2; }
      });
      if (!okc) continue;
      if (c.sameLength) { if (from.length !== to.length) continue; s += 1; }
      if (c.dir) { if (dirOf(from.length, to.length) !== c.dir) continue; s += 1; }
      if (s > bestScore) { best = c; bestScore = s; }
    }
    return best;
  }

  function resolveBadgeCopy(stage, from, toMember, size, preorder) {
    var b = bank();
    if (!b) return null;
    var to = { length: toMember.length, feature: toMember.feature };
    var cell = pickCell(stage, from, to);
    if (!cell || !cell.variants || !cell.variants.length) return null;
    var v = cell.variants[Math.floor(_pageSeed * 7919) % cell.variants.length];
    var scene = typeof v.s === 'string' ? v.s : (v.s[seasonGroup()] || v.s.north || '');
    var facts = (v.f || []).map(function (id) { return b.facts[id]; }).filter(Boolean).slice(0, 3);
    var kicker = v.k, close = v.c;
    if (preorder) {
      kicker = b.preorderKicker || v.k;
      var wk = preorder.eta ? weeksAt(preorder.eta) : null;
      close = (wk && b.preorderClose.due) ? b.preorderClose.due : b.preorderClose.base;
      close = close.replace(/\{ship\}/g, preorder.ship || 'soon').replace(/\{week\}/g, wk || '');
    }
    return {
      cellId: cell.id,
      kicker: fillTokens(kicker, size),
      scene: fillTokens(scene, size),
      facts: facts.map(function (f) { return fillTokens(f, size); }),
      close: fillTokens(close, size)
    };
  }

  // The change, named — "7/8 length" / "pockets" / "shorts, no pockets".
  // Shared by the switch button and the card kicker so both name the same
  // destination.
  function switchNameFor(from, m) {
    var lenChanged = m.length !== from.length;
    var featChanged = m.feature !== from.feature;
    if (lenChanged && featChanged) return m.length + ', ' + m.feature;
    // "full length" and the shorts values already name themselves — only
    // bare fractions get the word appended ("Switch to full length length"
    // bug, tired-mum review 3 Aug).
    if (lenChanged) return /length|shorts/.test(m.length) ? m.length : m.length + ' length';
    if (featChanged) return m.feature === 'with pockets' ? 'pockets' : 'no pockets';
    return null;
  }

  // What the switch button says; pre-order targets become a reserve action
  // with the ship window.
  function buttonLabelFor(from, target, preorder) {
    var what = switchNameFor(from, target.m) || target.label;
    if (preorder) return 'Reserve ' + what + ' — ships ' + (preorder.ship || 'soon');
    return 'Switch to ' + what;
  }

  // Card kicker, personalised to the switch (Mark, 24 Aug): the bank's
  // audience ("Why pregnant mums switch" / "What new mums pick") plus the
  // destination the button names — "Most pregnant mums switch to shorts
  // because:". Kickers matching neither pattern (the pre-order "Worth the
  // short wait") render untouched.
  function switchKicker(kicker, what) {
    if (!what) return kicker;
    var m = /^why\s+(.+?)\s+switch$/i.exec(kicker || '');
    if (m) return 'Most ' + m[1] + ' switch to ' + what + ' because:';
    m = /^what\s+(.+?)\s+pick$/i.exec(kicker || '');
    if (m) return 'Most ' + m[1] + ' pick ' + what + ' because:';
    return kicker;
  }

  function pickBadgeTarget(inStock, preorderFlat) {
    if (inStock.length) {
      return { target: inStock.slice().sort(function (a, b) {
        return (b.qty || 0) - (a.qty || 0) || (a.diffs - b.diffs) || (a.len - b.len);
      })[0], preorder: null };
    }
    if (preorderFlat.length) {
      var t = preorderFlat[0];
      return { target: t, preorder: { ship: t.ship, eta: t.eta } };
    }
    return null;
  }

  // Grow the target's chip into the persuasion card (State C / pending — the
  // ATC is dead there). Layout (Mark, 24 Aug): the ready-now chips stay an
  // uninterrupted list under their intro line; the card renders LAST in the
  // status block, directly above the dead ATC — list, then pitch, then
  // action. The target's own chip still leaves the list (the card IS that
  // option). Runs BEFORE wireChips so the card (es-chip class + data-handle)
  // gets the standard swap handler; the inner switch button is a visual
  // affordance whose click bubbles to it.
  function decorateBadge(alt, fam, size, from, inStock, preorderFlat) {
    if (window.emLang === 'de') return;                 // English bank only
    var stage = stageOfProduct(fam.current.p);
    var picked = pickBadgeTarget(inStock, preorderFlat);
    if (!picked || !picked.target || !picked.target.m) return;
    var target = picked.target, preorder = picked.preorder;
    var copy = resolveBadgeCopy(stage, from, target.m, size, preorder);
    if (!copy) return;
    var chip = alt.querySelector('.es-chip[data-handle="' + target.handle + '"]');
    if (!chip) return;
    var kicker = preorder ? copy.kicker : switchKicker(copy.kicker, switchNameFor(from, target.m));
    var card = document.createElement('div');
    card.className = 'es-chip es-badge';
    card.setAttribute('role', 'button');
    card.setAttribute('tabindex', '0');
    card.setAttribute('data-handle', target.handle);
    card.innerHTML = '<span class="es-bk">' + escHtml(kicker) + '</span>'
      + '<span class="es-bs">' + escHtml(copy.scene) + '</span>'
      + copy.facts.map(function (f) { return '<span class="es-bf">' + escHtml(f) + '</span>'; }).join('')
      + '<span class="es-bc">' + escHtml(copy.close) + '</span>'
      + '<span class="es-swbtn">' + escHtml(buttonLabelFor(from, target, preorder)) + '</span>';
    var row = chip.parentNode;
    chip.remove();
    if (!row.querySelector('.es-chip')) {
      // the target was the row's only chip — an intro line over an empty row
      // reads broken, so both go with it
      var lbl = row.previousElementSibling;
      row.remove();
      if (lbl && lbl.classList && lbl.classList.contains('es-pos')) lbl.remove();
    }
    alt.appendChild(card);
    card.addEventListener('keydown', function (ev) {
      if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); card.click(); }
    });
    card.addEventListener('click', function () {
      emit('fc_badge_tap', { cell: copy.cellId, from_handle: fam.current.handle, to_handle: target.handle, size: size || '', preorder: preorder ? 'yes' : 'no' });
    });
    emit('fc_badge_shown', { cell: copy.cellId, handle: fam.current.handle, to_handle: target.handle, size: size || '', preorder: preorder ? 'yes' : 'no' },
      'bg|' + fam.current.handle + '|' + (size || '') + '|' + target.handle + '|' + copy.cellId);
  }

  // ── The alternatives half of the status block (#em-status-alt) ─────────
  // Three writers, one owner:
  //   pending   "no pockets is not available in shorts" + ready-in chips
  //   preorder  line 3 under packprice's limited-release lines — where her
  //             size ships NOW inside the family
  //   sold_out  her selected combo is dead and not pre-orderable — say so
  //             plainly + ready-in chips
  function renderStatusAlt(fam) {
    var alt = document.getElementById('em-status-alt');
    if (!alt) return;
    alt.innerHTML = '';
    var info = window.__emPreorderInfo || {};
    var size = copySize();
    var what = ((window.pdpMeta && (window.pdpMeta.coreName || window.pdpMeta.title)) || '').toLowerCase();

    if (fam && pending) {
      // While a pending choice is on screen the limited-release lines for the
      // combo she moved AWAY from would contradict it — blank packprice's
      // half; restoreSelection()/the next size tap rewrites it.
      var mainEl = document.getElementById('em-status-main');
      if (mainEl) mainEl.innerHTML = '';
      var pChips = pendingChips(fam, size);
      var pGroups = preorderGroups(fam, size, pending);
      var sizeBit = size ? 'Size ' + escHtml(size) + ' ' : '';
      // Headline. Best case: "Size XXS 3/4 only comes with pockets." — when
      // every member carrying her tapped value shares ONE other-axis value
      // she isn't on, say so plainly (it also sells the switch as the only
      // path). Otherwise fall back to naming what she's holding ("no pockets
      // is not available in shorts"). Only axes that render as rows count —
      // a single-colour family never says "in black".
      // Feature values carry their own "with": 'with pockets' stays as-is,
      // 'no pockets' becomes "with no pockets" — never "with with pockets".
      var phraseOf = function (axKey, val) {
        if (axKey === 'feature') return val.indexOf('with') === 0 ? val : 'with ' + val;
        return 'in ' + val;
      };
      var only = [];
      var held = [];
      AXES.forEach(function (ax) {
        if (ax.key === pending.axis) return;
        if (distinctValues(fam, ax).length < 2) return;
        var vals = {};
        fam.members.forEach(function (m) { if (m[pending.axis] === pending.value) vals[m[ax.key]] = 1; });
        var keys = Object.keys(vals);
        if (keys.length === 1 && keys[0] !== fam.current[ax.key]) only.push(phraseOf(ax.key, keys[0]));
        held.push(phraseOf(ax.key, fam.current[ax.key]));
      });
      var head = only.length
        ? '<div class="es-line">' + sizeBit + escHtml(pending.value) + ' only comes ' + escHtml(only.join(' ')) + '.</div>'
        : '<div class="es-line">' + sizeBit + escHtml(pending.value) + ' is not available'
          + (held.length ? ' ' + escHtml(held.join(' ')) : '') + '.</div>';
      alt.innerHTML = head
        + (pChips.length
          ? '<div class="es-line es-pos">Ready to ship now in:</div><div class="es-chips">' + chipsHtml(pChips, true) + '</div>'
          : '')
        + preorderGroupsHtml(pGroups);
      // from = the combo she ASKED for (current with the pending axis applied)
      var pFrom = { length: fam.current.length, feature: fam.current.feature };
      if (pending.axis === 'length' || pending.axis === 'feature') pFrom[pending.axis] = pending.value;
      decorateBadge(alt, fam, size, pFrom, pChips, flatGroups(pGroups));
      wireChips(alt, fam, size, 'pending');
      // Her pending choice is not addable — the ATC follows the message.
      var atc = document.getElementById('add-to-cart-button');
      if (atc) {
        atc.classList.add('disabled');
        atc.textContent = (window.emStrings && window.emStrings.soldOut) || 'SOLD OUT';
      }
      emit('fc_alt_shown', { kind: 'pending', handle: fam.current.handle, size: size || '', axis: pending.axis, value: pending.value },
        'pd|' + fam.current.handle + '|' + (size || '') + '|' + pending.axis + '|' + pending.value);
    } else if (fam && info.state === 'preorder' && size) {
      var bChips = altChips(fam, size);
      if (bChips.length) {
        // Her pick is a live pre-order sale. Same presentation as State C
        // (Mark, 24 Aug — the accordion is retired): the label keeps the
        // YOUR PICK / OR READY TO SHIP TODAY reading grammar, then the
        // ready-now list as image tiles, then the persuasion card last,
        // directly above the live pre-order ATC.
        alt.innerHTML = '<div class="es-lbl">Or ready to ship today</div>'
          + '<div class="es-chips">' + chipsHtml(bChips, true) + '</div>';
        decorateBadge(alt, fam, size, { length: fam.current.length, feature: fam.current.feature }, bChips, []);
        wireChips(alt, fam, size, 'preorder');
        emit('fc_alt_shown', { kind: 'preorder', handle: fam.current.handle, size: size, chips: bChips.map(function (c) { return c.handle; }).join(',') },
          'pb|' + fam.current.handle + '|' + size);
      }
    } else if (fam && info.state === 'sold_out' && size) {
      var cChips = altChips(fam, size);
      var cGroups = preorderGroups(fam, size);
      var headline = '<div class="es-line">Size ' + escHtml(size) + ' in ' + escHtml(what || fam.range) + ' is out of stock.</div>';
      alt.innerHTML = headline
        + (cChips.length
          ? '<div class="es-line es-pos">Ready to ship now in:</div><div class="es-chips">' + chipsHtml(cChips, true) + '</div>'
          : '')
        + preorderGroupsHtml(cGroups);
      decorateBadge(alt, fam, size, { length: fam.current.length, feature: fam.current.feature }, cChips, flatGroups(cGroups));
      wireChips(alt, fam, size, 'oos');
      emit('fc_alt_shown', { kind: 'oos', handle: fam.current.handle, size: size, chips: cChips.map(function (c) { return c.handle; }).join(',') },
        'po|' + fam.current.handle + '|' + size);
    }
    syncBlockVisibility();
  }

  function render() {
    var sizeRow = document.getElementById('size-buttons');
    var host = document.getElementById(HOST_ID);
    var handle = currentHandle();
    var fam = handle ? familyFor(handle) : null;
    if (!sizeRow || !fam) {
      if (host) host.remove();
      var r2gone = document.getElementById('em-family-ring2');
      if (r2gone) r2gone.remove();
      // No family → no alternatives half; packprice's main half still shows.
      pending = null;
      var altGone = document.getElementById('em-status-alt');
      if (altGone) altGone.innerHTML = '';
      syncBlockVisibility();
      return;
    }

    ensureStyles();
    if (!host) {
      host = document.createElement('div');
      host.id = HOST_ID;
      sizeRow.parentNode.insertBefore(host, sizeRow.nextSibling);
    }
    host.innerHTML = '';

    // The size the rows speak about = the one the buy box is presenting.
    var size = activeSize();
    // Ring 2 jumps OUT of the range into another lifestage — too aggressive
    // to fire off a size she never actually chose, so it stays on the
    // trusted pick.
    var trusted = herSize();
    var currentDead = size ? !inStockAt(fam.current, size) : false;
    var esc = currentDead ? escapes(fam, size) : [];
    var escKey = {};
    esc.forEach(function (e) { escKey[e.axis + '|' + e.value] = true; });

    // Whole family dead in her size -> Ring 2 (forward-stage rescue cards).
    var familyDead = false;
    if (trusted && !inStockAt(fam.current, trusted)) {
      familyDead = true;
      for (var fi = 0; fi < fam.members.length; fi++) {
        if (inStockAt(fam.members[fi], trusted)) { familyDead = false; break; }
      }
    }
    renderRing2(fam, trusted, familyDead);

    AXES.forEach(function (ax) {
      var values = distinctValues(fam, ax);
      if (values.length < 2) return;                      // no structural choice — row absent
      var pendHere = pending && pending.axis === ax.key;
      var shown = [];
      values.forEach(function (v) {
        var isCurrent = v === fam.current[ax.key];
        var avail = !size || anyWithValueInSize(fam, ax.key, v, size);
        if (!avail && !isCurrent) return;                  // D1: hidden, not struck
        shown.push({ v: v, isCurrent: isCurrent, avail: avail });
      });
      if (!shown.length) return;
      if (shown.length === 1 && shown[0].isCurrent && shown[0].avail && values.length < 3) {
        // lone healthy current chip carries no information — skip the row
        return;
      }
      var lbl = document.createElement('div');
      lbl.className = 'fc-lbl';
      lbl.textContent = ax.label;
      host.appendChild(lbl);
      var row = document.createElement('div');
      row.className = 'fc-row';
      shown.forEach(function (o) {
        var b = document.createElement('button');
        b.type = 'button';
        var isDot = ax.key === 'colour' && SWATCH[o.v];
        b.className = isDot ? 'fc-dot' : 'fc-opt';
        if (isDot) { b.style.background = SWATCH[o.v]; b.setAttribute('aria-label', o.v); b.title = o.v; }
        else b.textContent = o.v;
        // Selection highlight: a pending tap on this axis moves the highlight
        // to the pending value — the current value shows unselected so the UI
        // reads "you chose this, here's where it's available".
        if (pendHere ? (o.v === pending.value) : o.isCurrent) b.classList.add('sel');
        if (o.isCurrent && !o.avail) b.classList.add('dead');
        if (escKey[ax.key + '|' + o.v]) b.classList.add('go');
        b.addEventListener('click', function (ev) {
          ev.preventDefault();
          if (o.isCurrent) {
            // Re-tapping her actual combo abandons a pending selection.
            if (pending) { pending = null; restoreSelection(); schedule(); }
            return;
          }
          var t = exactMember(fam, ax.key, o.v);
          // D5.1 stock-aware landing (Mark's bug, 24 Aug; extended 27 Aug):
          // her tapped value is only VISIBLE because some member carrying it
          // SELLS at her size (D1) — so the tap must never land on a dead
          // page while a buyable one exists. When the identity-exact member
          // is dead in her size ("full length" on dead twilight → dead
          // twilight full length, while a black full length is buyable),
          // land on the closest buyable member with her tapped value: the
          // tapped axis is exactly honoured, un-tapped axes move only as far
          // as availability requires.
          var rerouted = false;
          if (t && size && !inStockAt(t, size)) {
            var live = bestBuyableWith(fam, ax.key, o.v, size);
            if (live && live.handle !== t.handle) { t = live; rerouted = true; }
          }
          if (t && t.handle !== fam.current.handle) {
            // Combo resolved → this IS variant selection; swap. The status
            // block explains the landing product's availability (in stock:
            // nothing / pre-order lines / out-of-stock + chips).
            emit('fc_option_tap', {
              axis: ax.key, value: o.v, from_handle: fam.current.handle, to_handle: t.handle,
              size: size || '', was_rescue: escKey[ax.key + '|' + o.v] ? 'yes' : 'no',
              stock_reroute: rerouted ? 'yes' : 'no'
            });
            pending = null;
            // preserveScroll: the shopper is standing at the option rows — the
            // swap happens under her thumb with the existing loading veil; the
            // page must not move (mobile taps were jumping to the top).
            if (typeof window._v2SwapToHandle === 'function') window._v2SwapToHandle(t.handle, { preserveScroll: true });
            else location.href = '/products/' + t.handle;
          } else if (!t) {
            // No exact combo → NO silent jump (D5 strict, 3 Aug). Pending
            // selection: highlight her choice, say where it IS available,
            // let HER tap a chip to change.
            emit('fc_pending_set', { axis: ax.key, value: o.v, from_handle: fam.current.handle, size: size || '' });
            pending = { axis: ax.key, value: o.v };
            schedule();
          }
        });
        row.appendChild(b);
      });
      host.appendChild(row);
    });

    renderStatusAlt(fam);
  }

  // Re-render whenever the world changes underneath us.
  var t = null;
  function schedule() { clearTimeout(t); t = setTimeout(render, 60); }
  window.addEventListener('em:size:selected', function () { pending = null; schedule(); });
  window.addEventListener('em:v2:swapped', function () { pending = null; schedule(); });
  window.addEventListener('em:status:changed', schedule);
  window.addEventListener('popstate', schedule);
  function boot() {
    var sr = document.getElementById('size-buttons');
    if (sr) new MutationObserver(schedule).observe(sr, { childList: true });
    schedule();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
